// SPDX-License-Identifier: LicenseRef-MMS-WIRE
// Copyright (c) 2016-2026 Metal Management Solutions (Pty) Ltd

import {Component, OnDestroy, DestroyRef, inject, OnInit, ViewEncapsulation} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatest, of, Subject} from "rxjs";
import {auditTime, debounceTime, filter, switchMap, take, tap} from "rxjs/operators";
import {PlantDataService} from "../services/plant-data/plant_data.service";
import {AppScope} from "../services/app_scope.service";
import {ApiService} from "../services/api/api.service";
import {HeaderDataService} from "../services/header_data.service";
import {
    FolderEntry,
    MenuTreeService,
    StaticEntry,
    StaticFolderEntry, TreeEntry
} from "../services/menu-tree.service";
import {AccountService} from "../services/account.service";
import {UserDataService} from "../services/user_data.service";
import {ActivationEnd, Router, RouterLink} from "@angular/router";
import {getAccountFilter, getContainsFilter, getRelationWithNameFilter} from "../services/api/filter_utils";
import {deepCopy} from "../lib/utils";
import {FormDialogService} from "../services/form-dialog.service";
import {UserService} from "../services/user.service";
import {SearchQueryOptions} from "../services/api/search-query-options";
import {MatTreeNestedDataSource} from "@angular/material/tree";
import {SessionState} from "../_models/session-state";
import {Folder} from "../_models/folder";
import {SharedModule} from "../shared/modules/shared-module.module";
import {StaticMenuTreeComponent} from "./static-menu-tree/static-menu-tree.component";
import {DragdropTreeComponent} from "./dragdrop-tree/dragdrop-tree.component";
import {TreeNodeComponent} from "./tree_node.componen";
import {CommonModule} from "@angular/common";

@Component({
    selector: 'sidenavigation',
    templateUrl: 'side-nav.component.html',
    styleUrls: ['./side-nav.component.less'],
    imports: [
        CommonModule,
        SharedModule,
        RouterLink,
        StaticMenuTreeComponent,
        DragdropTreeComponent,
        TreeNodeComponent
    ],
    encapsulation: ViewEncapsulation.None
})

export class SideNavigation implements OnInit {
    private readonly destroyRef = inject(DestroyRef);
    events: string[] = [];
    opened: boolean;
    reports: any[];
    plants: any[];
    selected_plant: any;
    user: any;
    restricted: boolean;

    dataSource = new MatTreeNestedDataSource();
    topLevelFolderNode: FolderEntry;
    menu_groups: { [key: string]: string };
    mobile: boolean = false;
    show_plant: boolean = false;

    selected_account_id: string;
    process_search: string = '';
    search_results: any[] = [];
    session_search: string = '';
    session_results: any[] = [];
    session_data: { [key: string]: SessionState[] };
    sidenav_selected_item: string; //For current session_state id
    isDragging: boolean = false;
    editMode: boolean = false;
    private processSearchSubject: Subject<string> = new Subject<string>();
    private sessionSearchSubject: Subject<string> = new Subject<string>();
    private _prunedFolders: TreeEntry[];
    private _allFolders: TreeEntry[];
    staticFolders: (StaticFolderEntry | StaticEntry)[];
    pinnedItems: (StaticFolderEntry | StaticEntry)[];

    constructor(
        public plantData: PlantDataService,
        public appScope: AppScope,
        private api: ApiService,
        public headerData: HeaderDataService,
        private menuTreeService: MenuTreeService,
        private accountService: AccountService,
        private userData: UserDataService,
        private router: Router,
        private formDialogService: FormDialogService,
        public userService: UserService) {

        this.router.events.pipe(
            filter(event => event instanceof ActivationEnd),
            takeUntilDestroyed(this.destroyRef))
            .subscribe((event: ActivationEnd) => {
                this.sidenav_selected_item = event.snapshot.url[event.snapshot.url.length - 1]?.path;
            })
    }


    ngOnInit() {
        const ctrl = this;
        this.appScope.auth_complete.promise.then(() => {
            ctrl.restricted = ctrl.appScope.current_user.restricted_access;
            ctrl.user = ctrl.appScope.current_user;
            ctrl.mobile = !ctrl.appScope.isNotMobile;

            ctrl.menu_groups = this.headerData.folder_options();
            const $session_states = this.menuTreeService.sessionStatesChanged.pipe(
                takeUntilDestroyed(this.destroyRef), tap((result) => {
                    this.session_data = result;
                })).subscribe();

            // Single combineLatest over the pruned tree, the full tree, and
            // the active account. `menuTreeChanged` and `menuTreeChangedFull`
            // are both ReplaySubject(1) and emit back-to-back synchronously
            // from `menuTreeService.refresh()`; `auditTime(0)` coalesces
            // those two emissions into one microtask tick so the subscriber
            // sees both fresh values together and `_prunedFolders` /
            // `_allFolders` can never be out of sync (the previous two-sub
            // implementation rendered with a stale `_allFolders` for one
            // tick on every refresh in edit mode).
            combineLatest([
                this.menuTreeService.menuTreeChanged,
                this.menuTreeService.menuTreeChangedFull,
                this.accountService.activeAccountChanged,
            ])
                .pipe(
                    auditTime(0),
                    takeUntilDestroyed(this.destroyRef),
                )
                .subscribe(([prunedTree, fullTree, active_account]) => {
                    if (!active_account) {
                        // the active account has not been determined yet
                        return;
                    }
                    if (ctrl.restricted) {
                        ctrl.buildRestrictedMenu(prunedTree, active_account.account_id);
                    } else {
                        this.buildMenu(prunedTree, active_account.account_id);
                    }
                    // `_setTopLevelNode` swaps the whole `topLevelFolderNode`
                    // object on each edit-mode toggle, so this captured
                    // array reference stays pointing at the pruned contents
                    // even after edit mode replaces them on screen.
                    this._prunedFolders = this.topLevelFolderNode?.contents;

                    const accountFolders = fullTree[active_account.account_id];
                    this._allFolders = accountFolders || [];

                    this._updateFolderDisplay();

                    // Edit mode is per-account, and it does not end when a page-view is
                    // destroyed — so switching to an account where this user cannot edit would
                    // otherwise leave the sidebar editing with the toggle that turns it off
                    // already hidden (`canToggleFolderEditMode` re-evaluates per change
                    // detection, `editMode` does not). Rights resolve against
                    // `appScope.active_account_id`, which AccountService sets from its own
                    // subscriber, registered in its constructor and therefore ahead of this one.
                    if (this.editMode && !this.canToggleFolderEditMode()) {
                        this.headerData.folder_edit_toggle.next(false);
                    }
                });

            this.accountService.activeAccountChanged.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((active_account) => {
                ctrl.selected_account_id = active_account.account_id;
                ctrl.plantData.process_focus = null; //TODO Check this on url paste if using authguards
                this.session_search = '';
                this.session_results = [];

                ctrl.plantData.getPlants(ctrl.selected_account_id)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(plants => {
                        this.plants = plants;
                        ctrl.appScope.plants = plants;
                        ctrl.appScope.selected_plant = plants[0];
                        ctrl.plants = plants;
                        ctrl.selected_plant = ctrl.appScope.selected_plant;
                        plants.forEach(p => this.appScope.processTreeDataDict[p.id] = this.appScope.getProcessDictItem(p, true))
                    });

                this.search_results = [];
                this.process_search = '';

                const processLightOptions = new SearchQueryOptions();
                processLightOptions.ignore_routing = true;
                processLightOptions.filters = [
                    getAccountFilter(ctrl.selected_account_id)
                ];

                const processOptions = new SearchQueryOptions();
                processOptions.ignore_routing = true;
                processOptions.filters = [
                    getRelationWithNameFilter('component_type', 'ReportTree'),
                    getAccountFilter(active_account.account_id)
                ];

                this.api.process.searchMany(processOptions).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(response => {
                    ctrl.reports = response.data;
                    ctrl.reports.map(item => {
                        item.is_report = true;
                        this.appScope.processTreeDataDict[item.id] = this.appScope.getProcessDictItem(item, true)
                    });
                });

                this.menuTreeService.refresh();
            });

            this.appScope.sidenavOpenSubject.pipe(takeUntilDestroyed(this.destroyRef), tap(value => {
                if(value && this.editMode){
                    this.menuTreeService.refresh();
                }
            })).subscribe();
        });

        this.processSearchSubject.pipe(
            takeUntilDestroyed(this.destroyRef),
            debounceTime(300),
            switchMap(search_string => {
                if (this.process_search.length < 1) {
                    this.search_results = [];
                    return of([]);
                }
                let options = new SearchQueryOptions();
                options.filters = [{
                    and: [getAccountFilter(this.selected_account_id),
                        {or: [getContainsFilter('name', search_string), getContainsFilter('description', search_string)]}
                    ]
                }]
                return this.api.process_light.searchMany(options).pipe(take(1), tap(result => {
                    result.data.forEach(p => {
                        const item = this.appScope.getProcessDictItem(p, false);
                        if (this.search_results.map(s => s.id).indexOf(item.id) < 0) {
                            this.search_results.push(item)
                        }
                    })
                    this.search_results = this.search_results.slice(0, 50);
                }))
            })).subscribe();

        this.sessionSearchSubject.pipe(
            takeUntilDestroyed(this.destroyRef),
            debounceTime(300)
        ).subscribe(search_string => {
            if (this.session_search.length < 1 || !this.selected_account_id || !this.session_data) {
                this.session_results = [];
                return;
            }
            this.session_results = deepCopy(this.session_data[this.selected_account_id]);
            this.session_results = this.session_results.filter(item => {
                return item.attributes.name?.toLowerCase().indexOf(this.session_search.toLowerCase()) > -1;
            }).sort((a, b) => {
                return a.attributes.name > b.attributes.name ? 1 : -1;
            });
            this.session_results = this.session_results.slice(0, 50);
        });

        this.headerData.folder_edit_toggle.pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(value => {
                this.editMode = value;
                this._updateFolderDisplay();
            });

    }

    /**
     * True when the sidebar may offer its own edit-mode toggle.
     *
     * Same gate as the header's 'Toggle Edit Mode' button (page-view.setOptions):
     * restricted-access users are excluded regardless of features, because edit
     * mode swaps their pruned tree for the full unpruned folder list.
     *
     * Reads `appScope.current_user` rather than the `user` field so the control
     * resolves correctly before `auth_complete` has populated it — the template
     * asks on every change detection, not once.
     */
    canToggleFolderEditMode(): boolean {
        if (!this.appScope.current_user || this.appScope.current_user.restricted_access) return false;
        return this.userService.canEnterEditMode();
    }

    /**
     * The sidebar's own way in and out of folder edit mode, independent of whether
     * a page-view is mounted at all.
     *
     * Before this, `folder_edit_toggle` was emitted ONLY by PageViewComponent, so
     * the folder tree could only be edited while standing on a page-view route and
     * silently switched itself off on any navigation. Page edit mode still drives
     * this subject (PageViewComponent.toggleEditMode) — turning page editing off
     * still turns folder editing off — but it is not the only writer.
     */
    toggleFolderEditMode(): void {
        this.headerData.folder_edit_toggle.next(!this.headerData.folder_edit_toggle.getValue());
    }

    buildRestrictedMenu(account_folders, active_account_id: string) {
        if (this.user.json && this.user.json.length > 0) {
            const folder_name: string = this.menu_groups['my_views'] ? this.menu_groups['my_views'] : 'My Views';

            let folder = new StaticFolderEntry(folder_name);
            this.user.json.forEach(item => {
                let static_entry = new StaticEntry(item.name, item.url);
                folder.contents.push(static_entry);
            });
            if (!account_folders[active_account_id]) {
                account_folders[active_account_id] = [];
            }

            account_folders[active_account_id].unshift(folder);
        }

        this._setTopLevelNode(account_folders[active_account_id]);
    }

    buildMenu(account_folders, active_account_id: string) {
        const ctrl = this;

        if (!this.restricted) {
            if (!this.staticFolders?.length) {
                this.staticFolders = [];
            }
            if (!this.staticFolders['built']) {
                let folder = new StaticFolderEntry(
                    ctrl.menu_groups['insights'] ? ctrl.menu_groups['insights'] : 'Insights',
                    this.buildStatic('insights'));
                this.staticFolders.push(folder);

                if (!ctrl.mobile) {
                    folder = new StaticFolderEntry(
                        ctrl.menu_groups['internal controls'] ? ctrl.menu_groups['internal controls'] : 'Internal controls',
                        this.buildStatic('internal controls'));
                    this.staticFolders.push(folder);

                    folder = new StaticFolderEntry(
                        ctrl.menu_groups['wire modules'] ? ctrl.menu_groups['wire modules'] : 'Wire modules',
                        this.buildStatic('wire modules')
                    );

                    this.staticFolders.push(folder);

                    folder = new StaticFolderEntry(
                        ctrl.menu_groups['configuration'] ? ctrl.menu_groups['configuration'] : 'Configuration',
                        this.buildStatic('configuration'));
                    this.staticFolders.push(folder);

                    folder = new StaticFolderEntry(
                        ctrl.menu_groups['planning'] ? ctrl.menu_groups['planning'] : 'Planning',
                        this.buildStatic('planning'));
                    this.staticFolders.push(folder);

                    this.staticFolders['built'] = true;
                }
            }
        }

        let pinnedEntry = new StaticEntry('All Pages', '/view/page_list_view');
        this.pinnedItems = [pinnedEntry];
        this._setTopLevelNode(account_folders[active_account_id]);
    }

    private _updateFolderDisplay() {
        const folders = this.editMode ? this._allFolders : this._prunedFolders;
        if (folders) {
            this._setTopLevelNode(folders);
        }
    }

    private _setTopLevelNode(folders: TreeEntry[]) {
        // Synthetic root so the template can bind <dragdrop-tree
        // [node]="topLevelFolderNode">. DragdropTreeComponent only ever reads
        // node.contents / node.total_page_count (never node.folder), but build a
        // real FolderEntry from a placeholder Folder so the @Input() FolderEntry
        // contract holds — including once strictTemplates is enabled.
        const placeholder = new Folder();
        placeholder.id = 'top';
        const node = new FolderEntry(placeholder);
        node.contents = folders;
        node.menu_entry_count = folders?.length ?? 0;
        this.topLevelFolderNode = node;
    }

    buildStatic(folder) {
        const ctrl = this;
        let contents = [];
        switch (folder) {
            case 'insights' :
                contents.push(new StaticEntry('Quick Charts', '/view/quick_charts/'));
                if (!ctrl.mobile) {
                    contents.push(
                        new StaticEntry('Analysis Tools', '/view/analysis_tools'),
                        new StaticEntry('Value Driver Trees', '/view/value_driver_trees/'),
                        new StaticEntry('Data Exceptions', '/view/data_exceptions/'),
                        new StaticEntry(this.appScope.clientDict("component-events-side-menu"), '/view/component_events/'),
                    );
                }
                break;
            case 'internal controls' :
                contents.push(
                    new StaticEntry('Audit History', '/view/audit_history/'),
                    new StaticEntry('Automated Raw Data Collection Events', '/view/collection_events/'),
                    new StaticEntry('Background Jobs', '/view/jobs'),
                    new StaticEntry('Calculation Validation Check', '/view/calculation_check/'),
                    new StaticEntry('Component Template Check', '/view/validate_component_templates/'),
                    new StaticEntry('Lock Templates', '/view/lock_template_table_view/'),
                    new StaticEntry('Lock Policies', '/view/lock_policy_table_view/'),
                    new StaticEntry('Model Pivot Tables', '/view/event_pivot'),
                    new StaticEntry('Process Access', '/view/process_access_sheet/'),
                    new StaticEntry('User Permission Insights', '/view/audit_pivot/')
                );
                break;
            case 'wire modules' :
                contents.push(
                    new StaticEntry('Equipment List', '/view/equipment_sheet/'),
                    new StaticEntry('Event Types', '/view/event_types/'),
                    new StaticEntry(this.appScope.clientDict("component-types-side-menu"), '/view/component_types/'),
                );
                break;
            case 'configuration' :
                contents.push(
                    new StaticEntry(this.appScope.clientDict("component-events-side-menu"), '/view/component_events/'),
                    new StaticEntry('Default Shift Times', '/view/shift_sheet/'),
                    new StaticEntry('Data Source Mapping Table', '/view/collector_mapper_sheet/'),
                    new StaticEntry('Event Types', '/view/event_types/'),
                    new StaticEntry('Process List', '/view/edit_process/'),
                    new StaticEntry('Process Streams', '/view/edit_streams/'),
                    new StaticEntry('Report Configuration', '/view/edit_series_components/'),
                    new StaticEntry('Series List', '/view/edit_series/'),
                    new StaticEntry('Estimates', '/view/estimate_sheet/'),
                    new StaticEntry('Solver Templates', '/view/solver_templates/'),
                );
                break;
            case 'planning' :
                contents.push(
                    //new StaticEntry(this.appScope.clientDict("single-component-events-side-menu"), '/view/single_component_events/'),
                    new StaticEntry('Targets / Estimates', '/view/estimate_sheet/'),
                    //new StaticEntry('Recovery Grade Forecast (beta)', '/view/recovery_grade_forecast/'),
                );
                break;
        }

        return contents;
    }

    openCreateMenu(event: MouseEvent) {
        this.formDialogService.openFolderContextMenu(event, {
            inputs: {
                mode: 'create',
                canCreatePage: this.userService.canCreatePage(),
                canCreateFolder: this.userService.canCreateFolder(),
            },
            onCreatePage: () => this.openNewPage(),
            onCreateFolder: () => this.formDialogService.openFolderDialog(),
        });
    }

    // The sidebar's own `+` create-page: file the page and stay put.
    //
    // Same reversal of F47, and for the same reason, as `dragdrop-tree.openNewPageInFolder` — see
    // the comment there. This `+` sits beside the search box in the folder tree and creates folders
    // too, so it belongs to the same folder-tidying gesture, and navigating away from it would load
    // a page the user never asked for (Qase C480). The edit-mode half of the original reason is
    // gone — navigation does not end the sidebar's edit mode.
    // `page-view.openNewPageForm()` is the one that still navigates.
    async openNewPage() {
        const dialogRef = await this.formDialogService.openNewPageDialog();
        dialogRef.afterClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(result => {
            if (result) {
                this.menuTreeService.refresh();
            }
        });
    }

    searchProcesses() {
        this.processSearchSubject.next(this.process_search);
    }

    searchSessionStates() {
        this.sessionSearchSubject.next(this.session_search);
    }

    closeSidenav() {
        if (!this.isDragging) { // Only close if not dragging
            this.appScope.sidenavOpenSubject.next(false);
            this.appScope.sidenav_open = false;
        }
    }
}
